import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Loader2, CheckCircle, AlertCircle, ArrowLeft } from "lucide-react";
import { verify2fa } from "@/api/auth";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

export default function Verify2FA() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setUser } = useApp();
  const { t } = useTranslation();
  const isOperatorFlow =
    location.pathname === "/operator/verify-2fa" || location.state?.role === "operator";
  const isAdminFlow = location.state?.role === "admin";
  const signInPath = isAdminFlow ? "/kxt-mgmt/login" : (isOperatorFlow ? "/operator/login" : "/signin");
  
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const isDark = theme === "dark";
  const pageTitle = isOperatorFlow
    ? "Operator Two-Factor Authentication"
    : t("verify2fa.title", { defaultValue: "Two-Factor Authentication" });
  const heading = t("verify2fa.heading", { defaultValue: "Two-Factor Verification" });
  const subheading = isOperatorFlow
    ? "Enter your code from TOTP."
    : t("verify2fa.subheading", { defaultValue: "Enter your code from TOTP." });

  useEffect(() => {
    const token = location.state?.verificationToken || localStorage.getItem("verificationToken");
    if (!token) {
      navigate(signInPath);
    }
  }, [navigate, location.state, signInPath]);

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple chars
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    const verificationCode = code.join("");
    
    if (verificationCode.length !== 6) {
      setError(t("verify2fa.errorLength", { defaultValue: "Please enter all 6 digits." }));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const verificationToken = location.state?.verificationToken || localStorage.getItem("verificationToken");
      if (!verificationToken) throw new Error("Session expired. Please sign in again.");

      const role = isOperatorFlow ? "operator" : location.state?.role;
      const response = await verify2fa({ verificationToken, code: verificationCode }, role);

      if (!response.success) {
        throw new Error(response.message || "Verification failed");
      }
      const userData = (response.user || response.data?.user) as unknown as Record<string, unknown> | undefined;

      if (!userData) {
        throw new Error("Invalid user data received");
      }
      
      setSuccess(true);
      
      const profile = userData.profile as { firstName?: string; lastName?: string; avatar?: string } | undefined;
      const user = {
        id: (userData._id as string) || (userData.id as string),
        name: profile?.firstName && profile?.lastName
            ? `${profile.firstName} ${profile.lastName}`
            : profile?.firstName || (userData.email as string).split("@")[0],
        email: userData.email as string,
        role: userData.role as "admin" | "organization" | "operator" | "end-user",
        firstName: profile?.firstName,
        lastName: profile?.lastName,
        emailVerified: (userData.isVerified as boolean) || (userData.emailVerified as boolean),
        avatar: profile?.avatar || (userData.avatar as string),
        initials: profile?.firstName && profile?.lastName
            ? `${profile.firstName[0]}${profile.lastName[0]}`.toUpperCase()
            : profile?.firstName
            ? profile.firstName.substring(0, 2).toUpperCase()
            : (userData.email as string).substring(0, 2).toUpperCase(),
      };

      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
      }

      setUser(user);
      localStorage.removeItem("verificationToken");

      setTimeout(() => {
        let path = "/user";
        if (user.role === "operator") path = "/operator";
        else if (user.role === "admin") path = "/kxt-mgmt";
        else if (user.role === "organization") path = "/organization";
        
        navigate(path, { replace: true });
      }, 1500);

    } catch (err: unknown) {
      setError((err instanceof Error ? err.message : "An error occurred") || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle} – KonectX</title>
      </Helmet>

      <div
        className="min-h-screen flex flex-col items-center justify-center p-4 pt-24 sm:pt-4 transition-colors duration-300 bg-gradient-to-br from-blue-500 via-purple-500 to-purple-600"
      >
        {success && (
          <div className="fixed top-6 right-6 z-50">
            <div className="bg-green-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-right">
              <CheckCircle className="w-6 h-6" />
              <div>
                <p className="font-semibold">{t("verify2fa.success", { defaultValue: "Verification Successful!" })}</p>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={() => {
            localStorage.removeItem("verificationToken");
            navigate(signInPath);
          }}
          className="absolute top-6 left-4 sm:left-6 flex items-center gap-2 transition text-sm sm:text-base"
          style={{ color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)" }}
        >
          <ArrowLeft size={18} />
          <span className="hidden xs:inline">{t("verify2fa.backToSignIn", { defaultValue: "Back to Sign In" })}</span>
          <span className="xs:hidden">{t("login.tablogin", { defaultValue: "Login" })}</span>
        </button>

        <div className="w-full max-w-md">
          <div
            className="rounded-3xl shadow-2xl p-6 sm:p-10 transition-colors duration-300"
            style={{ backgroundColor: isDark ? "#333436" : "#ffffff" }}
          >
            <div className="mb-8 text-center">
              <h2
                className="text-2xl font-bold mb-2"
                style={{ color: isDark ? "#ffffff" : "#1f2937" }}
              >
                {heading}
              </h2>
              <p style={{ color: isDark ? "#9ca3af" : "#4b5563" }}>
                {subheading}
              </p>
            </div>

            {error && (
              <div
                className="mb-6 p-4 rounded-lg flex items-start gap-3"
                style={{
                  backgroundColor: isDark ? "rgba(220,38,38,0.12)" : "#fef2f2",
                  border: isDark ? "1px solid rgba(220,38,38,0.35)" : "1px solid #fecaca",
                }}
              >
                <AlertCircle
                  className="w-5 h-5 flex-shrink-0 mt-0.5"
                  style={{ color: isDark ? "#f87171" : "#dc2626" }}
                />
                <p className="text-sm" style={{ color: isDark ? "#f87171" : "#dc2626" }}>
                  {error}
                </p>
              </div>
            )}

            <form onSubmit={handleVerify} className="space-y-6">
              <div className="flex justify-between gap-1.5 sm:gap-2">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-10 sm:w-12 h-12 sm:h-14 text-center text-xl font-bold rounded-xl outline-none transition"
                    style={{
                      backgroundColor: isDark ? "#2a2c2e" : "#f3f4f6",
                      color: isDark ? "#ffffff" : "#1f2937",
                      boxShadow: "inset 0 0 0 2px transparent",
                    }}
                    onFocus={(e) => (e.target.style.boxShadow = "inset 0 0 0 2px #2563eb")}
                    onBlur={(e) => (e.target.style.boxShadow = "inset 0 0 0 2px transparent")}
                    disabled={loading || success}
                  />
                ))}
              </div>

              <Button
                type="submit"
                disabled={loading || success || code.some(d => !d)}
                className="w-full text-white font-semibold py-4 rounded-xl transition disabled:opacity-50"
                style={{ backgroundColor: isDark ? "#2563eb" : "#000000" }}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : success ? (
                  t("verify2fa.successBtn", { defaultValue: "Verified!" })
                ) : (
                  t("verify2fa.verifyBtn", { defaultValue: "Verify Code" })
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
